/**
 * Goods receiving.
 *
 * The organising rule, stated once here because the rest follows from it:
 * **a draft moves no stock**. Lines are typed or scanned while the delivery is
 * being checked against the invoice, and the ledger is untouched until someone
 * posts. Posting is the single act that writes `receipt` movements, it is
 * idempotent, and what it produces is immutable.
 */

import { z } from 'zod';
import {
  isoDateTimeSchema,
  longTextSchema,
  nonNegativeMoneySchema,
  shortTextSchema,
  uuidSchema,
} from '../primitives.js';
import { barcodeSchema } from './shop.js';

// ── Suppliers ───────────────────────────────────────────────────────────────

export const createSupplierSchema = z.object({
  name: shortTextSchema,
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().email().max(200).nullable().optional().or(z.literal('')),
  address: longTextSchema.nullable().optional(),
  /** The reference the business itself uses for this supplier. */
  supplierCode: z.string().trim().max(60).nullable().optional(),
  isActive: z.boolean().default(true),
});
export type CreateSupplierInput = z.infer<typeof createSupplierSchema>;

export const updateSupplierSchema = createSupplierSchema.partial();
export type UpdateSupplierInput = z.infer<typeof updateSupplierSchema>;

export const supplierSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  supplierCode: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Supplier = z.infer<typeof supplierSchema>;

// ── Receipts ────────────────────────────────────────────────────────────────

export const RECEIPT_STATUSES = ['draft', 'posted', 'cancelled'] as const;
export type ReceiptStatus = (typeof RECEIPT_STATUSES)[number];

export const startReceiptSchema = z.object({
  supplierId: uuidSchema.nullable().optional(),
  supplierInvoiceRef: z.string().trim().max(80).nullable().optional(),
  branchId: uuidSchema.nullable().optional(),
  receivedAt: isoDateTimeSchema.optional(),
  note: longTextSchema.nullable().optional(),
  /**
   * De-duplication key for the CREATE itself.
   *
   * Covers the case where the first response was lost and the client never
   * learned the receipt id it should have retried against — without it, a
   * retry opens a second delivery note for the same van.
   */
  idempotencyKey: z.string().trim().min(8).max(120).optional(),
});
export type StartReceiptInput = z.infer<typeof startReceiptSchema>;

/**
 * Add to a receipt, by product or by barcode.
 *
 * `quantity` adds to whatever the line already holds rather than replacing it,
 * because that is what a second scan of the same carton means. `setQuantity`
 * is the correction path, for when somebody miscounted and wants to state the
 * figure outright.
 */
export const receiptLineSchema = z
  .object({
    productId: uuidSchema.optional(),
    barcode: barcodeSchema.optional(),
    quantity: z.number().finite().positive().optional(),
    setQuantity: z.number().finite().nonnegative().optional(),
    unitCost: nonNegativeMoneySchema.nullable().optional(),
    note: longTextSchema.nullable().optional(),
  })
  .refine((line) => Boolean(line.productId) !== Boolean(line.barcode), {
    message: 'Identify the item by product or by barcode, not both',
    path: ['barcode'],
  })
  .refine((line) => line.quantity === undefined || line.setQuantity === undefined, {
    message: 'Send either a quantity to add or a quantity to set, not both',
    path: ['setQuantity'],
  });
export type ReceiptLineInput = z.infer<typeof receiptLineSchema>;

export const postReceiptSchema = z.object({
  note: longTextSchema.nullable().optional(),
});
export type PostReceiptInput = z.infer<typeof postReceiptSchema>;

export const receiptLineViewSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  productName: z.string(),
  productBarcode: z.string().nullable(),
  quantity: z.number(),
  unitCost: z.string().nullable(),
  lineTotal: z.string().nullable(),
  note: z.string().nullable(),
  /** The ledger row this line produced. Null until posted. */
  movementId: uuidSchema.nullable(),
  /** On-hand for this product right now, so a delivery can be sanity-checked. */
  onHand: z.number().nullable(),
});
export type ReceiptLineView = z.infer<typeof receiptLineViewSchema>;

export const receiptSchema = z.object({
  id: uuidSchema,
  supplierId: uuidSchema.nullable(),
  supplierName: z.string().nullable(),
  supplierInvoiceRef: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  status: z.enum(RECEIPT_STATUSES),
  receivedAt: z.string(),
  receivedBy: z.string().nullable(),
  postedAt: z.string().nullable(),
  postedBy: z.string().nullable(),
  note: z.string().nullable(),
  currencyCode: z.string(),
  lines: z.array(receiptLineViewSchema),
  totals: z.object({
    lines: z.number(),
    units: z.number(),
    /** What the delivery cost, at the unit costs on the lines. */
    value: z.string(),
    /** Lines with no unit cost — they post, but contribute nothing to value. */
    linesWithoutCost: z.number(),
  }),
});
export type Receipt = z.infer<typeof receiptSchema>;

export const receiptSummarySchema = z.object({
  id: uuidSchema,
  supplierName: z.string().nullable(),
  supplierInvoiceRef: z.string().nullable(),
  branchName: z.string().nullable(),
  status: z.enum(RECEIPT_STATUSES),
  receivedAt: z.string(),
  receivedBy: z.string().nullable(),
  postedAt: z.string().nullable(),
  postedBy: z.string().nullable(),
  lines: z.number(),
  units: z.number(),
  value: z.string(),
});
export type ReceiptSummary = z.infer<typeof receiptSummarySchema>;
